'use client'

import { useEffect, useId, useRef, useState } from 'react'
import Link from 'next/link'
import { WaitlistSubscribeResponseSchema } from '@mist/contracts/promotion'
import { ApiError, postApi } from '@/lib/api-client'
import { captureAttribution } from '@/lib/attribution'
import { attributionProperties, trackApiError, trackEvent } from '@/lib/analytics'

/**
 * MIST-0403 waitlist CTA (R-EXP-02, R-EXP-03). Email-only, with explicit
 * launch-email consent separate from optional marketing consent (consent
 * separation, R-COMP-03). On success it transitions instantly to the payoff
 * state instructing verification and never reveals whether the email already
 * exists (R-EXP-02, Section 16.3). Honeypot feeds fraud signals (R-COMP-06).
 */
type Phase = 'idle' | 'submitting' | 'done'
type FieldError = { field: 'email' | 'launchEmailConsent' | 'form'; message: string } | null

const NO_JS_CONTACT_ACTION = '/no-js/contact?topic=launch-list'
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export default function WaitlistForm() {
  const emailId = useId()
  const consentId = useId()
  const marketingId = useId()
  const honeypotId = useId()
  const emailErrorId = useId()
  const consentErrorId = useId()
  const formErrorId = useId()
  const [phase, setPhase] = useState<Phase>('idle')
  const [fieldError, setFieldError] = useState<FieldError>(null)
  const started = useRef(false)
  const honeypotRef = useRef<HTMLInputElement>(null)
  const outcomeHeadingRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    if (phase === 'done') outcomeHeadingRef.current?.focus()
  }, [phase])

  function onFirstInteraction() {
    if (started.current) return
    started.current = true
    trackEvent('mist_waitlist_started', attributionProperties(captureAttribution()))
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setFieldError(null)
    const form = new FormData(event.currentTarget)
    const email = String(form.get('email') ?? '').trim()
    const launchEmailConsent = form.get('launchEmailConsent') === 'true'
    const marketingConsent = form.get('marketingConsent') === 'true'

    if (!email) {
      setFieldError({ field: 'email', message: 'Enter your email address.' })
      return
    }
    if (!EMAIL_PATTERN.test(email)) {
      setFieldError({ field: 'email', message: 'Enter a valid email address.' })
      return
    }
    if (!launchEmailConsent) {
      setFieldError({ field: 'launchEmailConsent', message: 'Please confirm you want the launch email so we can send the confirmation step.' })
      return
    }
    if (honeypotRef.current?.value) {
      // Honeypot tripped — present success without contacting the API.
      setPhase('done')
      return
    }

    setPhase('submitting')
    const attribution = captureAttribution()
    try {
      const result = await postApi('/api/v1/waitlist', WaitlistSubscribeResponseSchema, {
        email,
        launchEmailConsent: true,
        marketingConsent,
        attribution,
        idempotencyKey: crypto.randomUUID(),
      })
      trackEvent('mist_waitlist_submitted', attributionProperties(attribution))
      if (result.verificationEmailQueued) trackEvent('mist_verification_sent', { route: attribution.landingPath })
      setPhase('done')
    } catch (caught) {
      if (!(caught instanceof ApiError)) throw caught
      trackApiError('/api/v1/waitlist', caught)
      setFieldError({ field: 'form', message: caught.message })
      setPhase('idle')
    }
  }

  const emailError = fieldError?.field === 'email' ? fieldError.message : null
  const consentError = fieldError?.field === 'launchEmailConsent' ? fieldError.message : null
  const formError = fieldError?.field === 'form' ? fieldError.message : null
  const liveMessage =
    phase === 'done'
      ? 'We sent a confirmation email. Your place on the launch list activates only after you confirm it.'
      : phase === 'submitting'
        ? 'Sending your launch email request.'
        : ''

  return (
    <div className="stack" style={{ maxWidth: phase === 'done' ? '32rem' : '30rem' }}>
      <p className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
        {liveMessage}
      </p>
      {phase === 'done' ? (
        <div className="stack">
          <h2 ref={outcomeHeadingRef} tabIndex={-1} className="eyebrow">
            Check your inbox
          </h2>
          <p className="lede">We sent a confirmation email. Your place on the launch list activates only after you confirm it.</p>
          <p className="hint">
            No email in a few minutes? Check spam, or reach us on the <Link href="/contact">Contact</Link> page.
          </p>
        </div>
      ) : (
        <>
          <noscript>
            <div className="stack measure" role="note" aria-label="JavaScript-disabled signup continuation">
              <p className="hint">
                Automatic launch-list signup requires JavaScript. Submitting continues to Contact without subscribing you automatically, so
                you can ask for manual help without putting your email in the URL.
              </p>
            </div>
          </noscript>
          <form
            method="post"
            action={NO_JS_CONTACT_ACTION}
            onSubmit={onSubmit}
            onFocusCapture={onFirstInteraction}
            className="stack"
            noValidate
          >
            <div className="field">
              <label htmlFor={emailId}>Email</label>
              <input
                id={emailId}
                name="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                required
                aria-invalid={emailError ? 'true' : undefined}
                aria-describedby={emailError ? emailErrorId : undefined}
              />
              {emailError ? (
                <p id={emailErrorId} className="error" role="alert">
                  {emailError}
                </p>
              ) : null}
            </div>
            <label className="consent" htmlFor={consentId}>
              <input
                id={consentId}
                type="checkbox"
                name="launchEmailConsent"
                value="true"
                required
                aria-invalid={consentError ? 'true' : undefined}
                aria-describedby={consentError ? consentErrorId : undefined}
              />
              <span>Email me when MIST by BDQ Edition 001 opens. I can unsubscribe at any time.</span>
            </label>
            {consentError ? (
              <p id={consentErrorId} className="error" role="alert">
                {consentError}
              </p>
            ) : null}
            <label className="consent" htmlFor={marketingId}>
              <input id={marketingId} type="checkbox" name="marketingConsent" value="true" />
              <span>Optionally, send me occasional MIST by BDQ news. This is separate and never required.</span>
            </label>
            <div className="hp-field" aria-hidden="true">
              <label htmlFor={honeypotId}>Company website</label>
              <input id={honeypotId} ref={honeypotRef} name="company_website" type="text" tabIndex={-1} autoComplete="off" />
            </div>
            {formError ? (
              <p id={formErrorId} className="error" role="alert">
                {formError}
              </p>
            ) : null}
            <button className="mist-button mist-button--primary" type="submit" disabled={phase === 'submitting'}>
              {phase === 'submitting' ? 'Sending…' : 'Get the launch email'}
            </button>
            <p className="hint">
              Confirmed by email (double opt-in). If JavaScript is disabled, use the <Link href="/contact">Contact</Link> page.
            </p>
          </form>
        </>
      )}
    </div>
  )
}
